import {
  type FreezerActivity,
  type FreezerActivityFilters,
  type FreezerPaginationMeta,
  cancelActivitiesPoll,
  clearFreezerActivities,
  fetchActivityActors,
  fetchActivityStatuses,
  fetchActivityTypes,
  pollActivities,
  selectFreezerActivitiesAsList,
  selectFreezerFilterActors,
  selectFreezerFilterStatuses,
  selectFreezerFilterTypes,
  selectFreezerPagination,
} from "@app/freezer";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import { tunaEvent } from "@app/tuna";
import type { SelectOption } from "@app/ui/shared/select";
import { CalendarDate, today } from "@internationalized/date";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RangeValue } from "react-aria-components";
import { useSearchParams } from "react-router-dom";
import { usePoller } from "./use-poller";

export function formatDateForApi(date: CalendarDate): string {
  const year = date.year;
  const month = String(date.month).padStart(2, "0");
  const day = String(date.day).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateFromUrl(dateStr: string | null): CalendarDate | null {
  if (!dateStr) return null;
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  return new CalendarDate(
    Number.parseInt(year, 10),
    Number.parseInt(month, 10),
    Number.parseInt(day, 10),
  );
}

function getDefaultDateRange(): RangeValue<CalendarDate> {
  const todayDate = today("UTC");
  return {
    start: todayDate.subtract({ days: 6 }),
    end: todayDate,
  };
}

function parseListParam(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim().toString())
    .filter(Boolean);
}

export interface UseActivityFiltersConfig {
  orgId: string;
  fixedFilters?: Partial<
    Pick<
      FreezerActivityFilters,
      "envs" | "targetTypes" | "resourceType" | "resourceId"
    >
  >;
  hideFilters?: boolean;
  limit?: number;
}

export interface AppliedExportFilters {
  organizationId: string;
  occurredAfter: string;
  occurredBefore: string;
  types?: string[];
  statuses?: string[];
  actors?: string[];
  envs?: string[];
  q?: string;
  readOnly?: boolean;
}

export interface UseActivityFiltersResult {
  activities: FreezerActivity[];
  isLoading: boolean;
  dateRange: RangeValue<CalendarDate>;
  onDateRangeChange: (range: RangeValue<CalendarDate> | null) => void;
  pagination: FreezerPaginationMeta;
  onPageChange: (page: number) => void;
  dateWarning: string | null;
  onDismissWarning: () => void;
  typeOptions: SelectOption[];
  statusOptions: SelectOption[];
  actorOptions: SelectOption[];
  stagedTypes: string[];
  stagedStatuses: string[];
  stagedActors: string[];
  stagedEnvs: string[];
  stagedReadOnly: boolean;
  onTypesChange: (values: string[]) => void;
  onStatusesChange: (values: string[]) => void;
  onActorsChange: (values: string[]) => void;
  onEnvsChange: (values: string[]) => void;
  onReadOnlyChange: (value: boolean) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  hasPendingChanges: boolean;
  showEnvFilter: boolean;
  hideFilters: boolean;
  appliedExportFilters: AppliedExportFilters;
}

export function useActivityFilters({
  orgId,
  fixedFilters,
  hideFilters = false,
  limit,
}: UseActivityFiltersConfig): UseActivityFiltersResult {
  const [params, setParams] = useSearchParams();
  const [dateWarning, setDateWarning] = useState<string | null>(null);

  const showEnvFilter = !fixedFilters?.envs;

  // Fetch filter options
  useQuery(fetchActivityTypes());
  useQuery(fetchActivityStatuses());
  useQuery(fetchActivityActors());

  const typeOptions = useSelector(selectFreezerFilterTypes);
  const statusOptions = useSelector(selectFreezerFilterStatuses);
  const actorOptions = useSelector(selectFreezerFilterActors);

  // URL params
  const occurredAfter = params.get("occurred_after");
  const occurredBefore = params.get("occurred_before");
  const pageParam = params.get("page");
  const page = pageParam ? Number.parseInt(pageParam, 10) : 1;

  const typesParam = params.get("types");
  const statusesParam = params.get("statuses");
  const actorsParam = params.get("actors");
  const envsParam = params.get("envs");
  const qParam = params.get("q");
  const readOnlyParam = params.get("read_only");

  const appliedTypes = useMemo(() => parseListParam(typesParam), [typesParam]);
  const appliedStatuses = useMemo(
    () => parseListParam(statusesParam),
    [statusesParam],
  );
  const appliedActors = useMemo(
    () => parseListParam(actorsParam),
    [actorsParam],
  );
  const appliedEnvs = useMemo(() => parseListParam(envsParam), [envsParam]);
  const appliedReadOnly = useMemo(
    () => readOnlyParam === "true",
    [readOnlyParam],
  );

  // Staged filter state
  const [stagedTypes, setStagedTypes] = useState<string[]>(appliedTypes);
  const [stagedStatuses, setStagedStatuses] =
    useState<string[]>(appliedStatuses);
  const [stagedActors, setStagedActors] = useState<string[]>(appliedActors);
  const [stagedEnvs, setStagedEnvs] = useState<string[]>(appliedEnvs);
  const [stagedReadOnly, setStagedReadOnly] =
    useState<boolean>(appliedReadOnly);

  const dateRange = useMemo<RangeValue<CalendarDate>>(() => {
    const startDate = parseDateFromUrl(occurredAfter);
    const endDate = parseDateFromUrl(occurredBefore);
    if (startDate && endDate) {
      return { start: startDate, end: endDate };
    }
    return getDefaultDateRange();
  }, [occurredAfter, occurredBefore]);

  const [stagedDateRange, setStagedDateRange] =
    useState<RangeValue<CalendarDate>>(dateRange);

  // Sync staged state when URL params change externally (e.g. browser back/forward)
  useEffect(() => {
    setStagedTypes(appliedTypes);
    setStagedStatuses(appliedStatuses);
    setStagedActors(appliedActors);
    if (showEnvFilter) {
      setStagedEnvs(appliedEnvs);
    }
    setStagedReadOnly(appliedReadOnly);
    setStagedDateRange(dateRange);
  }, [
    appliedTypes,
    appliedStatuses,
    appliedActors,
    appliedEnvs,
    appliedReadOnly,
    dateRange,
    showEnvFilter,
  ]);

  // Auto-deselect read-only types when toggle is turned off
  useEffect(() => {
    if (!stagedReadOnly) {
      const readOnlyTypeValues = new Set(
        typeOptions.filter((opt) => opt.readOnly).map((opt) => opt.value),
      );
      setStagedTypes((prev) =>
        prev.filter((type) => !readOnlyTypeValues.has(type)),
      );
    }
  }, [stagedReadOnly, typeOptions]);

  const hasPendingChanges = useMemo(() => {
    const arraysEqual = (a: string[], b: string[]) =>
      a.length === b.length && [...a].sort().join() === [...b].sort().join();
    const dateRangeChanged =
      stagedDateRange.start.compare(dateRange.start) !== 0 ||
      stagedDateRange.end.compare(dateRange.end) !== 0;
    return (
      dateRangeChanged ||
      !arraysEqual(stagedTypes, appliedTypes) ||
      !arraysEqual(stagedStatuses, appliedStatuses) ||
      !arraysEqual(stagedActors, appliedActors) ||
      (showEnvFilter && !arraysEqual(stagedEnvs, appliedEnvs)) ||
      stagedReadOnly !== appliedReadOnly
    );
  }, [
    stagedDateRange,
    dateRange,
    stagedTypes,
    appliedTypes,
    stagedStatuses,
    appliedStatuses,
    stagedActors,
    appliedActors,
    stagedEnvs,
    appliedEnvs,
    stagedReadOnly,
    appliedReadOnly,
    showEnvFilter,
  ]);

  // Serialize fixed filter arrays to stable strings for dependency comparison,
  // since callers typically pass new array literals on each render.
  const fixedEnvsKey = fixedFilters?.envs?.join(",");
  const fixedTargetTypesKey = fixedFilters?.targetTypes?.join(",");
  const fixedResourceType = fixedFilters?.resourceType;
  const fixedResourceId = fixedFilters?.resourceId;

  const filters = useMemo<FreezerActivityFilters>(() => {
    const startDate = parseDateFromUrl(occurredAfter);
    const endDate = parseDateFromUrl(occurredBefore);
    const defaultRange = getDefaultDateRange();
    return {
      organizationId: orgId,
      occurredAfter: formatDateForApi(startDate || defaultRange.start),
      occurredBefore: formatDateForApi(endDate || defaultRange.end),
      page,
      types: appliedTypes.length > 0 ? appliedTypes : undefined,
      statuses: appliedStatuses.length > 0 ? appliedStatuses : undefined,
      actors: appliedActors.length > 0 ? appliedActors : undefined,
      envs:
        fixedFilters?.envs ??
        (appliedEnvs.length > 0 ? appliedEnvs : undefined),
      targetTypes: fixedFilters?.targetTypes,
      resourceType: fixedResourceType,
      resourceId: fixedResourceId,
      q: qParam || undefined,
      readOnly: appliedReadOnly || undefined,
    };
  }, [
    orgId,
    occurredAfter,
    occurredBefore,
    page,
    appliedTypes,
    appliedStatuses,
    appliedActors,
    appliedEnvs,
    appliedReadOnly,
    fixedEnvsKey,
    fixedTargetTypesKey,
    fixedResourceType,
    fixedResourceId,
    qParam,
  ]);

  const appliedExportFilters = useMemo<AppliedExportFilters>(
    () => ({
      organizationId: filters.organizationId,
      occurredAfter:
        filters.occurredAfter || formatDateForApi(getDefaultDateRange().start),
      occurredBefore:
        filters.occurredBefore || formatDateForApi(getDefaultDateRange().end),
      types: filters.types,
      statuses: filters.statuses,
      actors: filters.actors,
      envs: filters.envs,
      q: filters.q,
      readOnly: filters.readOnly,
    }),
    [filters],
  );

  const poller = useMemo(() => pollActivities(filters), [filters]);
  const cancel = useMemo(() => cancelActivitiesPoll(), []);
  usePoller({ action: poller, cancel });

  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      dispatch(clearFreezerActivities());
    };
  }, [dispatch]);

  const loader = useLoader(pollActivities);
  const allActivities = useSelector(selectFreezerActivitiesAsList);
  const activities = limit ? allActivities.slice(0, limit) : allActivities;
  const pagination = useSelector(selectFreezerPagination);

  const handleDateRangeChange = useCallback(
    (newRange: RangeValue<CalendarDate> | null) => {
      if (!newRange) return;
      setStagedDateRange(newRange);
    },
    [],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(params);
      newParams.set("page", newPage.toString());
      setParams(newParams, { replace: true });
    },
    [params, setParams],
  );

  const handleApplyFilters = useCallback(() => {
    dispatch(clearFreezerActivities());
    const newParams = new URLSearchParams(params);
    newParams.set("page", "1");

    newParams.set("occurred_after", formatDateForApi(stagedDateRange.start));
    newParams.set("occurred_before", formatDateForApi(stagedDateRange.end));

    // Filter out read-only types when the toggle is off
    const readOnlyTypeValues = new Set(
      typeOptions.filter((opt) => opt.readOnly).map((opt) => opt.value),
    );
    const effectiveTypes = stagedReadOnly
      ? stagedTypes
      : stagedTypes.filter((type) => !readOnlyTypeValues.has(type));

    const filterKeys: [string, string[]][] = [
      ["types", effectiveTypes],
      ["statuses", stagedStatuses],
      ["actors", stagedActors],
      ...(showEnvFilter ? [["envs", stagedEnvs] as [string, string[]]] : []),
    ];
    for (const [key, values] of filterKeys) {
      if (values.length) {
        newParams.set(key, values.join(","));
      } else {
        newParams.delete(key);
      }
    }

    if (stagedReadOnly) {
      newParams.set("read_only", "true");
    } else {
      newParams.delete("read_only");
    }

    tunaEvent(
      "activity-filter-query",
      new URLSearchParams(newParams).toString(),
    );

    setParams(newParams, { replace: true });
  }, [
    dispatch,
    params,
    stagedDateRange,
    stagedTypes,
    stagedStatuses,
    stagedActors,
    stagedEnvs,
    stagedReadOnly,
    setParams,
    showEnvFilter,
    tunaEvent,
    typeOptions,
  ]);

  const handleReset = useCallback(() => {
    dispatch(clearFreezerActivities());
    setStagedTypes([]);
    setStagedStatuses([]);
    setStagedActors([]);
    if (showEnvFilter) {
      setStagedEnvs([]);
    }
    setStagedReadOnly(false);
    const defaultRange = getDefaultDateRange();
    setStagedDateRange(defaultRange);
    const newParams = {
      occurred_after: formatDateForApi(defaultRange.start),
      occurred_before: formatDateForApi(defaultRange.end),
      page: "1",
    };
    setParams(newParams, { replace: true });

    // Fire tuna event for analytics tracking
    tunaEvent(
      "activity-filter-query",
      new URLSearchParams(newParams).toString(),
    );
  }, [dispatch, setParams, showEnvFilter]);

  // Date validation
  useEffect(() => {
    const startDate = parseDateFromUrl(occurredAfter);
    const endDate = parseDateFromUrl(occurredBefore);

    if ((occurredAfter || occurredBefore) && (!startDate || !endDate)) {
      const invalidValues: string[] = [];
      if (occurredAfter && !startDate) invalidValues.push(`"${occurredAfter}"`);
      if (occurredBefore && !endDate) invalidValues.push(`"${occurredBefore}"`);

      setDateWarning(
        `Invalid date ${invalidValues.length > 1 ? "values" : "value"} ${invalidValues.join(" and ")} in URL. Showing results for the last 7 days instead.`,
      );

      const defaultRange = getDefaultDateRange();
      const newParams = new URLSearchParams(params);
      newParams.set("occurred_after", formatDateForApi(defaultRange.start));
      newParams.set("occurred_before", formatDateForApi(defaultRange.end));
      setParams(newParams, { replace: true });
    }
  }, [occurredAfter, occurredBefore, params, setParams]);

  return {
    activities,
    isLoading: loader.isLoading,
    dateRange: stagedDateRange,
    onDateRangeChange: handleDateRangeChange,
    pagination,
    onPageChange: handlePageChange,
    dateWarning,
    onDismissWarning: () => setDateWarning(null),
    typeOptions,
    statusOptions,
    actorOptions,
    stagedTypes,
    stagedStatuses,
    stagedActors,
    stagedEnvs,
    stagedReadOnly,
    onTypesChange: setStagedTypes,
    onStatusesChange: setStagedStatuses,
    onActorsChange: setStagedActors,
    onEnvsChange: setStagedEnvs,
    onReadOnlyChange: setStagedReadOnly,
    onApplyFilters: handleApplyFilters,
    onResetFilters: handleReset,
    hasPendingChanges,
    showEnvFilter,
    hideFilters,
    appliedExportFilters,
  };
}
